import { StateService } from '@uirouter/core';
import { IPromise, IWindowService } from 'angular';
import * as angular from 'angular';
import UserService from '../user/user.service';
import {
  SessionDimensions,
  StatusLabel,
  IAnalyticsEvent,
  PageViewDimensions,
  PaymentMethodType,
  PurchaseEvent,
  Ecommerce,
  Product,
  Click,
  Add,
  Purchase,
  CancelPackageEvent,
  ChangePaymentMethod,
  ActionField,
  VirtualEvent
} from './events/dimensions';
import { PackageThumbnail, ListeningPackageType } from '../package/package.model';

/* @ngInject */
export default class GoogleAnalyticsService {

  private ecommerce:Ecommerce = new Ecommerce();
  private product:Product = new Product();
  private error:any;
  private cachedUserId: string = '';

  private packageSegmentEventMap: any = {
    'Aile_MONTHLY': 'fizy aile paket satış',
    'Bireysel_MONTHLY': 'fizy aylık bireysel paket satış',
    'Bireysel_YEARLY': 'fizy yıllık bireysel paket satış',
    'Duo_MONTHLY': 'fizy duo paket satış',
    'Öğrenci_MONTHLY': 'fizy öğrenci paket satış',
    'Premium+_MONTHLY': 'fizy premium+ paket satış',
    'Premium_MONTHLY': 'fizy Bireysel Sesli Kitap Paket Satış',
    'Standart_MONTHLY': 'fizy aylık bireysel paket satış',
    'Standart_YEARLY': 'fizy yıllık bireysel paket satış',
    'Standard_MONTHLY': 'fizy aylık bireysel paket satış',
    'Standard_YEARLY': 'fizy yıllık bireysel paket satış',
    'Individual_MONTHLY': 'fizy aylık bireysel paket satış',
    'Individual_YEARLY': 'fizy yıllık bireysel paket satış',
    'Student_MONTHLY': 'fizy öğrenci paket satış',
    'Family_MONTHLY': 'fizy aile paket satış'
  };

  constructor(
    private $window: IWindowService,
    private $state: StateService,
    private $stateParams:any,
    private userService: UserService
  ) {}

  public activate() {
    this.getDataLayer();
  }

  // Analytics must never break business flows: dataLayer may be absent when
  // the GTM/gtag snippets are not loaded (e.g. non-prod environments).
  private getDataLayer(): any[] {
    const w: any = this.$window;
    if (!w.dataLayer) {
      w.dataLayer = [];
    }
    return w.dataLayer;
  }

  private pushToDataLayer(event: any) {
    try {
      this.getDataLayer().push(event);
    } catch (e) {
      console.warn('dataLayer.push() failed:', e);
    }
  }

  public sendPageViewAnalytics(dimensions?: PageViewDimensions) {
    if (this.isDimensionsNeeded(this.$state.$current) && !dimensions) {
      return;
    }

    const pageViewEvent: any = {
      event: 'page_view',
      page_title: this.getVirtualPageTitle(this.$state.$current),
      page_path: this.$state.$current.name
    };

    if (dimensions) {
      pageViewEvent.source_name = dimensions.sourceName;
      pageViewEvent.source_type = dimensions.sourceType;
      if (dimensions.searchText) {
        pageViewEvent.search_text = dimensions.searchText;
      }
      if (dimensions.contentId) {
        pageViewEvent.content_id = dimensions.contentId;
      }
      if (dimensions.contentType) {
        pageViewEvent.content_type = dimensions.contentType;
      }
    }

    this.trackEvent(pageViewEvent);
  }

  public writeVirtualPages(event: VirtualEvent) {
    // Send directly to GA4 via gtag
    const gtag = (this.$window as any).gtag;
    if (gtag) {
      gtag('event', 'page_view', event);
    }
    this.pushToDataLayer(event);
  }

  public trackEvent(event: IAnalyticsEvent) {
    // Get user ID and send directly to GA4 via gtag if event name exists
    const gtag = (this.$window as any).gtag;
    if (gtag && (event as any).event) {
      const eventData: any = { ...event };
      const eventName = eventData.event;
      delete eventData.event;

      // Convert camelCase keys to snake_case for GA4 compatibility
      const normalizedData = Object.keys(eventData).reduce((acc: any, key: string) => {
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        acc[snakeKey] = eventData[key];
        return acc;
      }, {});

      // Add user_id if available (cached or fetch)
      if (this.cachedUserId) {
        normalizedData.user_id = this.cachedUserId;
      } else {
        // Try to get user info and cache it
        this.userService.getUserInfo().then(user => {
          if (user && user.msisdn) {
            this.cachedUserId = user.msisdn;
          }
        }).catch(() => {
          // Silently fail - proceed without user_id
        });
      }

      try {
        gtag('event', eventName, normalizedData);
      } catch (e) {
        console.error('gtag() error:', e);
      }
    }

    this.pushToDataLayer(event);
  }

  public getVirtualPageTitle(state:any): string {
    return (
      (state && state.data && state.data.ga && state.data.ga.title) ||
      'fizy Müzik & Video'
    );
  }

  private getSessionDimensions(): IPromise<SessionDimensions> {
    return this.userService.isAuthenticated().then(authenticated => {
      return {
        isOnline: 'Online',
        loginStatus: StatusLabel.TRUE
      };
    });
  }

  private isDimensionsNeeded(state:any): boolean {
    return state && state.data && state.data.ga && state.data.ga.dimensions;
  }

  public sendGAEvent(eventLabel:string,packet: PackageThumbnail,error?,paymentMethod?:string){
    var userPackage = localStorage.getItem('activePackages');
    var paymentMethodType;

    if(paymentMethod){
      paymentMethodType = paymentMethod === 'bill' ? PaymentMethodType.BILL : PaymentMethodType.CREDIT_CARD;
    } else{
      if(packet.type === ListeningPackageType.SDP || packet.type === ListeningPackageType.MOBILODEME){
        paymentMethodType = PaymentMethodType.BILL
      } else if (packet.type === ListeningPackageType.CREDITCARD){
        paymentMethodType = PaymentMethodType.CREDIT_CARD
      }
   }
    // CONTRACTEDYEARLY is a yearly plan, so it shares the YEARLY label
    const period = packet.period === 'CONTRACTEDYEARLY' ? 'YEARLY' : packet.period;
    const segmentKey = packet.packageSegment && period ? `${packet.packageSegment}_${period}` : null;
    const purchaseEvent: any = {
      event: 'purchase',
      value: packet.price ? parseFloat(packet.price.toString()) : 0,
      currency: packet.priceUnit || 'TRY',
      transaction_id: 'txn_' + Date.now() + '_' + packet.id,
      items: [{
        item_id: packet.id,
        item_name: packet.nameInTR || packet.name,
        price: packet.price ? parseFloat(packet.price.toString()) : 0,
        quantity: 1,
        item_category: 'subscription',
      }],
      eventLabel: eventLabel,
      eventCategory: segmentKey ? (this.packageSegmentEventMap[segmentKey] || packet.packageSegment) : 'Unknown',
      paymentMethod: paymentMethodType,
      package_segment: packet.packageSegment,
      package_period: packet.period,
      userPackage: userPackage
    };

    if(eventLabel === 'Failure'){
      purchaseEvent.errorType = (this.error && this.error.message) || (error && error.message) || 'An error occurred';
    }

    this.trackEvent(purchaseEvent);
  }

  public sendCancelPackage(eventLabel:string,packet: PackageThumbnail,error?){
    var userPackage = localStorage.getItem('activePackages');
    var paymentMethodType = packet.type === ListeningPackageType.CREDITCARD ? PaymentMethodType.CREDIT_CARD : PaymentMethodType.BILL;

    const cancelEvent: any = {
      event: 'cancel_package',
      value: packet.price ? parseFloat(packet.price.toString()) : 0,
      currency: packet.priceUnit || 'TRY',
      items: [{
        item_id: packet.id,
        item_name: packet.nameInTR || packet.name,
        price: packet.price ? parseFloat(packet.price.toString()) : 0,
        quantity: 1,
        item_category: 'subscription'
      }],
      eventLabel: eventLabel,
      paymentMethod: paymentMethodType,
      userPackage: userPackage
    };

    if(eventLabel === 'Failure'){
      cancelEvent.errorType = (this.error && this.error.message) || (error && error.message) || 'An error occurred';
    }

    this.trackEvent(cancelEvent);
  }

  public sendPaymentChangeEvent(eventLabel:string,packet: PackageThumbnail,error?){
    var userPackage = localStorage.getItem('activePackages');
    var paymentMethodType = PaymentMethodType.CREDIT_CARD;

    const paymentChangeEvent: any = {
      event: 'change_payment_method',
      value: packet.price ? parseFloat(packet.price.toString()) : 0,
      currency: packet.priceUnit || 'TRY',
      items: [{
        item_id: packet.id,
        item_name: packet.nameInTR || packet.name,
        price: packet.price ? parseFloat(packet.price.toString()) : 0,
        quantity: 1,
        item_category: 'subscription'
      }],
      eventLabel: eventLabel,
      paymentMethod: paymentMethodType,
      userPackage: userPackage
    };

    if(eventLabel === 'Failure'){
      paymentChangeEvent.errorType = (this.error && this.error.message) || (error && error.message) || 'An error occurred';
    }

    this.trackEvent(paymentChangeEvent);
  }

  public convertProductToPackage(packageProduct:PackageThumbnail,listName:string){
    var product :Product ={};
    product.name = packageProduct.nameInTR ? packageProduct.nameInTR : packageProduct.name;
    product.id = packageProduct.id;
    product.price = packageProduct.price;
    product.list = listName;

    product. brand='fizy';
    product. category='';
    product. variant='Packages';
    product. quantity=0;
    product. position=1;
    product. affiliation='';
    product. revenue='';
    product. tax='';
    product. shipping = 0;
    product. coupon='';
    return product;

  }

  public setEcommerce(packetEcommerce: PackageThumbnail, eventAction:string, listName:string, productList?: Product[]){
    var ecommerce : Ecommerce={};
    var product:Product ={};


    if(packetEcommerce){
    product = this.convertProductToPackage(packetEcommerce,listName);

    if(!packetEcommerce.priceUnit){
      ecommerce.currencyCode = 'TRY';
    }else{
      ecommerce.currencyCode = packetEcommerce.priceUnit;

    }
  }

    if(eventAction === 'Impression'){
      ecommerce.impressions = productList;

    }else if (eventAction === 'Click'){
      var click: Click = {};
      click.products = [];
      click.products .push(product);
      ecommerce.click = click;
    }else if(eventAction === 'Add to Card'){
      var add :Add = {};
      add.products =[];
      add.products.push(product);
      ecommerce.add = add;
    }else if (eventAction === 'Purchase' || eventAction === 'Cancel Package' ){
      var purchase:Purchase={};
      purchase.products = [];
      purchase.products.push(product);

      if(!!packetEcommerce &&!!packetEcommerce.price){

        var afield:ActionField={};
        afield.id=product.id;
        afield.revenue=packetEcommerce.price;
        purchase.actionField=afield;
      }

      ecommerce.purchase = purchase;
    }


    return ecommerce;


  }

  public setError(error){
    this.error = error;
  }

}



// WEBPACK FOOTER //
// ./src/app/common/services/analytics/google-analytics.service.ts